import React, { useContext, useEffect, useRef, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Divider from "@material-ui/core/Divider";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";

import ScheduleModal from "../ScheduleModal";
import LinkClienteModal from "../LinkClienteModal";
import SolicitacaoDesenvolvimentoPreviewModal from "../SolicitacaoDesenvolvimentoPreviewModal";
import {
	createSolicitacaoDesenvolvimento,
	getSolicitacaoDesenvolvimentoPreview,
} from "../../services/solicitacaoDesenvolvimento";

// extraItems: ações que não couberam na barra do cabeçalho e foram recolhidas
// para cá. Formato { key, label, onClick }. Opcional e com default [] — o
// render fica idêntico ao anterior quando ninguém passa nada.
// hasRealTicket: false quando o `ticket` é o pseudo-chamado da aba de Contatos
// (id "contact-<id>", sem linha em Tickets). Default true para não mudar nada
// para quem não passa a prop.
const TicketOptionsMenu = ({
	ticket,
	menuOpen,
	handleClose,
	anchorEl,
	extraItems = [],
	hasRealTicket = true,
}) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
	const [contactId, setContactId] = useState(null);
	const [linkClienteModalOpen, setLinkClienteModalOpen] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [previewData, setPreviewData] = useState(null);

	// Rearmar na entrada é obrigatório, não redundante: o Fast Refresh do dev
	// server roda o cleanup e re-executa o efeito PRESERVANDO os refs, então sem
	// esta linha o `isMounted` ficava false para sempre depois de qualquer hot
	// reload deste arquivo — e handleCloseLinkClienteModal/TransferTicketModal
	// viravam no-op, deixando o modal impossível de fechar. Vale também para o
	// StrictMode do React 18, que faz mount/unmount/mount.
	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleOpenScheduleModal = () => {
		handleClose();
		setContactId(ticket?.contact?.id);
		setScheduleModalOpen(true);
	}

	const handleCloseScheduleModal = () => {
		setScheduleModalOpen(false);
		setContactId(null);
	};

	const isGroupTicket = Boolean(ticket?.isGroup || ticket?.contact?.isGroup);

	// Com o chamado fechado o menu fica só com o que age sobre o contato/registro
	// (agendar, vincular a cliente, solicitar desenvolvimento). Ficam de fora:
	// - Transferir: o modal manda status "open" (TransferTicketModalCustom), ou
	//   seja, é uma reabertura disfarçada — e sem a checagem de
	//   /tickets/:id/reopen-availability que o botão "Reabrir" faz antes, falharia
	//   com ERR_OTHER_OPEN_TICKET quando já há atendimento em andamento.
	// - Deletar: apagar chamado encerrado não faz parte do fluxo de atendimento.
	const isClosedTicket = ticket?.status === "closed";

	const handleOpenLinkClienteModal = () => {
		handleClose();
		setLinkClienteModalOpen(true);
	};

	const handleCloseLinkClienteModal = () => {
		if (isMounted.current) {
			setLinkClienteModalOpen(false);
		}
	};

	const handleOpenSolicitacaoPreview = async () => {
		handleClose();

		if (!ticket?.id) {
			toast.error(i18n.t("tickets.developmentRequest.previewTicketNotFound"));
			return;
		}

		setPreviewOpen(true);
		setPreviewData(null);
		setPreviewLoading(true);

		try {
			const data = await getSolicitacaoDesenvolvimentoPreview(ticket.id);
			setPreviewData(data);
		} catch (err) {
			toastError(err);
			setPreviewOpen(false);
		} finally {
			setPreviewLoading(false);
		}
	};

	const handleCloseSolicitacaoPreview = () => {
		if (saveLoading) {
			return;
		}

		setPreviewOpen(false);
		setPreviewData(null);
	};

	const handleConfirmSolicitacao = async (observacao) => {
		if (!ticket?.id) {
			toast.error(i18n.t("tickets.developmentRequest.saveTicketNotFound"));
			return;
		}

		setSaveLoading(true);
		try {
			await createSolicitacaoDesenvolvimento(ticket.id, observacao);
			toast.success(i18n.t("tickets.developmentRequest.created"));
			setPreviewOpen(false);
			setPreviewData(null);
		} catch (err) {
			toastError(err);
		} finally {
			setSaveLoading(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				{/* Array achatado como filho direto do Menu, nunca um Fragment: o
				    MenuList faz React.Children.map nos filhos diretos e não desce
				    dentro de fragments (perderia o foco por teclado e o divisor). */}
				{extraItems.map(item => (
					<MenuItem
						key={item.key}
						onClick={() => {
							handleClose();
							item.onClick();
						}}
					>
						{item.label}
					</MenuItem>
				))}
				{extraItems.length > 0 && <Divider />}
				<MenuItem onClick={handleOpenScheduleModal}>
					{i18n.t("ticketOptionsMenu.schedule")}
				</MenuItem>
				{!isClosedTicket && (
					<MenuItem onClick={handleOpenTransferModal}>
						{i18n.t("ticketOptionsMenu.transfer")}
					</MenuItem>
				)}
				{!isGroupTicket && (
					<MenuItem onClick={handleOpenLinkClienteModal}>
						{i18n.t("ticketOptionsMenu.linkCliente")}
					</MenuItem>
				)}
				{hasRealTicket && (
					<MenuItem onClick={handleOpenSolicitacaoPreview}>
						{i18n.t("tickets.developmentRequest.request")}
					</MenuItem>
				)}
				{!isClosedTicket && (
					<Can
						role={user.profile}
						perform="ticket-options:deleteTicket"
						yes={() => (
							<MenuItem onClick={handleOpenConfirmationModal}>
								{i18n.t("ticketOptionsMenu.delete")}
							</MenuItem>
						)}
					/>
				)}
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket?.contact?.name || ''
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModalCustom
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticket={ticket}
			/>
			<ScheduleModal
				open={scheduleModalOpen}
				onClose={handleCloseScheduleModal}
				aria-labelledby="form-dialog-title"
				contactId={contactId}
			/>
			{!isGroupTicket && (
				<LinkClienteModal
					open={linkClienteModalOpen}
					onClose={handleCloseLinkClienteModal}
					contact={ticket?.contact}
				/>
			)}
			<SolicitacaoDesenvolvimentoPreviewModal
				open={previewOpen}
				onClose={handleCloseSolicitacaoPreview}
				onConfirm={handleConfirmSolicitacao}
				loading={previewLoading || saveLoading}
				previewData={previewData}
			/>
		</>
	);
};

export default TicketOptionsMenu;